<script setup lang="ts">
import LoadingButton from "@/components/LoadingButton.vue"
import { useVarsityAppConfig } from "@/composables"
import { defineTranslations } from "@/utils/defineTranslations"
import { sleep, useHttpClient, useLoadingState } from "@/vf"
import { ref } from "vue"

const props = defineProps<{
    endpoint: string
}>()

const emit = defineEmits<{
    (e: "booked"): void
}>()

const http = useHttpClient()
const { appConfig } = useVarsityAppConfig()

const loading = useLoadingState()
const amount = ref<number>()
const date = ref<string>()
const note = ref<string>()
const success = ref(false)

function book() {
    loading.while(async () => {
        if (!amount.value) {
            return
        }

        await http.post(props.endpoint, {
            amount: amount.value * 100,
            date: date.value,
            note: note.value,
        })

        amount.value = undefined
        date.value = undefined
        note.value = undefined

        emit("booked")

        success.value = true
        await sleep(3000)
        success.value = false
    })
}

const trans = defineTranslations({
    "title": {
        de: "Manuelle Zahlung buchen",
        en: "Book manual payment",
    },
    "amount": {
        de: "Betrag",
        en: "Amount",
    },
    "date": {
        de: "Zahlungsdatum (leer lassen für heute)",
        en: "Payment date (leave empty for today)",
    },
    "note_optional": {
        de: "Notiz (optional)",
        en: "Note (optional)",
    },
    "booked_by": {
        de: "Gebucht von",
        en: "Booked by",
    },
    "success": {
        de: "Zahlung erfolgreich gebucht",
        en: "Payment successfully booked",
    },
    "book": {
        de: "Buchen",
        en: "Book",
    },
})
</script>
<template>
    <form @submit.prevent="book">
        <div class="bg-primary-dark text-white px-4 py-2 font-weight-bold">
            {{ trans("title") }}
        </div>

        <div class="p-4">
            <b>{{ trans("amount") }}</b>
            <div class="input-group">
                <input
                    type="number"
                    step="0.01"
                    class="form-control d-inline-block text-right"
                    placeholder="0,00"
                    style="width: 20ch"
                    v-model.number="amount"
                />
                <div class="input-group-append">
                    <div class="input-group-text border-0">Euro</div>
                </div>
            </div>

            <b class="mt-4 d-block">{{ trans("note_optional") }}</b>
            <input type="text" class="form-control" v-model="note" />

            <b class="mt-4 d-block">{{ trans("date") }}</b>
            <input type="date" class="form-control" v-model="date" />

            <b class="mt-4 d-block">{{ trans("booked_by") }}</b>
            <input
                type="text"
                disabled
                class="form-control"
                style="opacity: 50%"
                :value="appConfig.login.username + ' (' + appConfig.login.userNumber + ')'"
            />

            <div class="d-flex justify-content-between mt-4">
                <div>
                    <span class="text-success" v-if="success">
                        {{ trans("success") }}
                    </span>
                </div>
                <div>
                    <LoadingButton :loading="loading" :disabled="!amount" type="submit">
                        {{ trans("book") }}
                    </LoadingButton>
                </div>
            </div>
        </div>
    </form>
</template>
